const apiUrl: string = 'https://npe-inc.zendesk.com/api/v2/help_center';

export class ZendeskCategory {
	public readonly videosElement: HTMLDivElement = this.sectionElement.querySelector('.product-videos');
	public readonly videosHeading: HTMLParagraphElement = this.videosElement.querySelector('p.h5');
	public readonly videosList: HTMLUListElement = this.videosElement.querySelector('ul');
	public readonly videoTemplate: HTMLTemplateElement = this.videosElement.querySelector('template');
	public readonly supportElement: HTMLDivElement = this.sectionElement.querySelector('.support-menu__list');
	public readonly supportMenuTemplate: HTMLTemplateElement = this.supportElement.querySelector('template');
	private readonly categoryId: string = this.sectionElement.dataset.category;
	private isLoaded: boolean = false;
	private videoSection: VideoSection;
	private sections: Section[];

	constructor(public readonly sectionElement: HTMLElement) {}

	public async load(): Promise<void> {
		if (this.isLoaded) return;
		this.isLoaded = true;

		try {
			// Load sections from API
			if (!this.categoryId) throw new Error('Missing category ID');
			const sectionsResponse = await fetch(`${apiUrl}/categories/${this.categoryId}/sections`);
			if (!sectionsResponse) throw new Error('Empty sections response');
			const {sections}: {sections: SectionData[]} = await sectionsResponse.json();
			const videoSection = sections.find((section) => /videos/i.test(section.name));
			const otherSections = videoSection ? sections.filter((section) => section.id !== videoSection.id) : sections;

			await Promise.all([
				(async () => {
					// Load video articles from API
					if (!videoSection) return;
					const videosResponse = await fetch(`${apiUrl}/articles/search?section=${videoSection.id}&per_page=8`);
					if (!videosResponse) throw new Error('Empty videos response');
					const videos: SearchData = await videosResponse.json();
					this.videoSection = new VideoSection(this, videoSection, videos.results);
					this.videosHeading.innerText += ` (${videos.count})`;
				})(),
				(async () => {
					// Load other articles from API
					if (!otherSections.length) return;
					const articlesResponse = await fetch(`${apiUrl}/articles/search?section=${otherSections.map((section) => section.id).join(',')}&per_page=50`);
					if (!articlesResponse) throw new Error('Empty articles response');
					const articles: SearchData = await articlesResponse.json();
					this.sections = otherSections.map((section) => new Section(this, section, articles.results)).sort((a, b) => a.data.position - b.data.position);
				})()
			]);
			this.render();
		} catch (error) {
			console.error(`Zendesk integration error: ${(error as Error)?.message}`, error);
			this.videosElement.style.display = 'none';
			this.supportElement.style.display = 'none';
		}
	}

	public render(): void {
		this.videoSection?.render();
		for (const section of this.sections) section.render();
	}
}

class Section {
	protected readonly articles: ArticleData[];

	constructor(public readonly product: ZendeskCategory, public readonly data: Readonly<SectionData>, articles: Readonly<ArticleData>[]) {
		this.articles = articles.filter((article) => article.section_id === this.data.id).sort((a, b) => a.position - b.position);
	}

	public render(): void {
		this.product.sectionElement.classList.add('has-articles');
		const sectionElement = this.product.supportMenuTemplate.content.cloneNode(true) as HTMLElement;
		const headingElement = sectionElement.querySelector('h2');
		const listElement = sectionElement.querySelector('ul');
		const itemTemplate = listElement?.querySelector('template');

		headingElement.innerText = this.data.name;
		for (const article of this.articles) {
			const itemElement = itemTemplate.content.cloneNode(true) as HTMLLIElement;
			const linkElement = itemElement.querySelector('a');
			linkElement.innerText = article.title;
			linkElement.href = article.html_url;
			linkElement.target = '_blank';
			listElement.append(itemElement);
		}
		this.product.supportElement.append(sectionElement);
	}
}

class VideoSection extends Section {
	private readonly videos: VideoThumbnail[];

	constructor(product: ZendeskCategory, data: SectionData, articles: ArticleData[]) {
		super(product, data, articles);
		this.videos = this.articles.map((article) => new VideoThumbnail(this, article));
	}

	public render(): void {
		this.product.sectionElement.classList.add('has-videos');
		for (const video of this.videos) video.render();
	}
}

class VideoThumbnail {
	private readonly youtubeId: string;
	private plyrEvent: CustomEvent;

	constructor(private readonly section: VideoSection, private readonly article: ArticleData) {
		const url = new URL('https:' + article.body.match(/"(https?:)?(\/\/(www\.)?youtu(\.be|be(-nocookie)?\.com|)\/.*?)"/i)?.[2] || '');
		this.youtubeId = url.searchParams.get('v') || url.pathname.slice(url.pathname.lastIndexOf('/') + 1);		

		this.plyrEvent = new CustomEvent('plyr-event', {
			detail: {
				id : this.youtubeId
			}
		});
	}

	public render(): void {
		const itemElement = this.section.product.videoTemplate.content.cloneNode(true) as HTMLLIElement;
		const imgElement = itemElement.querySelector('img');
		const videoCloseButton = itemElement.querySelector('.close-button');
		const videoElement = itemElement.querySelector('.plyr__video-embed');
		const videoWrapper = itemElement.querySelector('.zendesk-video');
		const captionElement = itemElement.querySelector('p');
		
		imgElement.src = `https://img.youtube.com/vi/${this.youtubeId}/hqdefault.jpg`;
		videoCloseButton.setAttribute('id',`ButtonStop-${this.youtubeId}`);
		videoElement.setAttribute('id',`player-${this.youtubeId}`);
		videoWrapper.setAttribute('id',`VideoEmbed-${this.youtubeId}`);
		videoElement.setAttribute('data-plyr-embed-id',this.youtubeId);		
		captionElement.innerText = this.article.title;
		this.section.product.videosList.append(itemElement);
		
		document.dispatchEvent(this.plyrEvent);
	}
}

/** Section data from Zendesk API */
interface SectionData {
	/** The id of the category to which this section belongs */
	category_id: number;

	/** The time at which the section was created */
	created_at: string;

	/** The description of the section */
	description: string;

	/** The url of this section in HC */
	html_url: string;

	/** Automatically assigned when creating subscriptions */
	id: number;

	/** The locale in which the section is displayed */
	locale: string;

	/** The name of the section */
	name: string;

	/** Whether the section is out of date */
	outdated: boolean;

	/** The id of the section to which this section belongs. Only writable for Guide Enterprise customers */
	parent_section_id: number;

	/** The position of this section in the section list. Used when sorting is set to ´manual´. By default the section is added to the end of the list */
	position: number;

	/** The source (default) locale of the section */
	source_locale: string;

	/** The theme template name used to display this section in Help Center. */
	theme_template: string;

	/** The time at which the section was last updated */
	updated_at: string;

	/** The API url of this section */
	url: string;
}

/** Article data from Zendesk API */
interface ArticleData {
	/** The id of the user who wrote the article (set to the user who made the request on create by default) */
	author_id: number;

	/** HTML body of the article. Unsafe tags and attributes may be removed before display. For a list of safe tags and attributes, see Allowing unsafe HTML in Help Center articles in Zendesk help */
	body: string;

	/** True if comments are disabled; false otherwise */
	comments_disabled: boolean;

	/** The list of content tags attached to the article */
	content_tag_ids: string[];

	/** The time the article was created */
	created_at: string;

	/** True if the translation for the current locale is a draft; false otherwise. false by default. Can be set when creating but not when updating. For updating, see Translations */
	draft: boolean;

	/** The time the article was last edited in its displayed locale */
	edited_at: string;

	/** The url of the article in Help Center */
	html_url: string;

	/** Automatically assigned when the article is created */
	id: number;

	/** An array of label names associated with this article. By default no label names are used. Only available on certain plans */
	label_names: string[];

	/** The locale that the article is being displayed in */
	locale: string;

	/** Deprecated. Always false because the source translation is always the most up-to-date translation */
	outdated: boolean;

	/** Locales in which the article was marked as outdated */
	outdated_locales: string[];

	/** The id of the permission group which defines who can edit and publish this article */
	permission_group_id: number;

	/** The position of this article in the article list. 0 by default */
	position: number;

	/** True if this article is promoted; false otherwise. false by default */
	promoted: boolean;

	/** The id of the section to which this article belongs */
	section_id: number;

	/** The source (default) locale of the article */
	source_locale: string;

	/** The title of the article */
	title: string;

	/** The time the article was last updated */
	updated_at: string;

	/** The API url of the article */
	url: string;

	/** The id of the user segment which defines who can see this article. Set to null to make it accessible to everyone */
	user_segment_id: number;

	/** The total number of upvotes and downvotes */
	vote_count: number;

	/** The sum of upvotes (+1) and downvotes (-1), which may be positive or negative */
	vote_sum: number;
}

interface SearchData {
	count: number;
	next_page: string;
	page: number;
	page_count: number;
	per_page: number;
	previous_page: string;
	results: ArticleData[];
}